<template>
    <div class="add-to-basket">
        <!-- Quantity Selector -->
        <div class="flex items-center mb-4" v-if="maxQuantity > 1">
            <span class="text-sm mr-3" :class="variant === 'light' ? 'text-white/80' : 'text-gray-600'">Quantity:</span>
            <div class="flex items-center rounded-lg" :class="variant === 'light' ? 'border border-white/30 bg-white/10' : 'border border-gray-300'">
                <button
                    @click="decrementQuantity"
                    :disabled="quantity <= 1"
                    class="px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    :class="variant === 'light' ? 'text-white hover:bg-white/20' : 'text-gray-600 hover:bg-gray-100'"
                >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                    </svg>
                </button>
                <span class="px-4 py-2 text-center min-w-[3rem] font-medium" :class="variant === 'light' ? 'text-white' : ''">{{ quantity }}</span>
                <button
                    @click="incrementQuantity"
                    :disabled="quantity >= maxQuantity"
                    class="px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    :class="variant === 'light' ? 'text-white hover:bg-white/20' : 'text-gray-600 hover:bg-gray-100'"
                >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                </button>
            </div>
            <span class="text-sm ml-3" :class="variant === 'light' ? 'text-white/70' : 'text-gray-500'">{{ maxQuantity }} available</span>
        </div>

        <!-- Add to Basket Button Row -->
        <div class="flex flex-wrap items-center gap-4">
            <button
                @click="addToBasket"
                :disabled="loading || !inStock"
                class="py-3 px-6 rounded-lg font-semibold text-white transition-colors"
                :class="[buttonClasses, buttonClass]"
            >
                <span v-if="loading" class="flex items-center justify-center">
                    <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    Adding...
                </span>
                <span v-else-if="added" class="flex items-center justify-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    Added to Basket!
                </span>
                <span v-else-if="!inStock">Out of Stock</span>
                <span v-else>Add to Basket</span>
            </button>

            <!-- Slot for inline content (e.g., "In Stock" text) -->
            <slot name="inline"></slot>
        </div>

        <!-- Error Message -->
        <p v-if="error" class="mt-2 text-sm text-red-600">{{ error }}</p>
    </div>
</template>

<script>
export default {
    name: 'AddToBasket',

    props: {
        sku: {
            type: String,
            required: true,
        },
        maxQuantity: {
            type: Number,
            default: 1,
        },
        inStock: {
            type: Boolean,
            default: true,
        },
        variant: {
            type: String,
            default: 'default', // 'default' or 'light' (for dark backgrounds)
        },
        buttonClass: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            quantity: 1,
            loading: false,
            added: false,
            error: null,
        };
    },

    computed: {
        buttonClasses() {
            if (!this.inStock) {
                return 'bg-gray-400 cursor-not-allowed';
            }
            if (this.added) {
                return 'bg-green-600';
            }
            return 'bg-brill-red hover:bg-brill-red-dark';
        },
    },

    methods: {
        incrementQuantity() {
            if (this.quantity < this.maxQuantity) {
                this.quantity++;
            }
        },

        decrementQuantity() {
            if (this.quantity > 1) {
                this.quantity--;
            }
        },

        async addToBasket() {
            if (this.loading || !this.inStock) return;

            this.loading = true;
            this.error = null;

            try {
                const response = await fetch('/basket/add', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    },
                    body: JSON.stringify({
                        sku: this.sku,
                        quantity: this.quantity,
                    }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || 'Failed to add to basket');
                }

                this.added = true;

                // Update basket count in header
                this.$emit('added', data.basketCount);
                window.dispatchEvent(new CustomEvent('basket-updated', {
                    detail: { count: data.basketCount }
                }));

                // Reset after 2 seconds
                setTimeout(() => {
                    this.added = false;
                }, 2000);

            } catch (err) {
                this.error = err.message;
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.bg-brill-red {
    background-color: #C41E3A;
}
.bg-brill-red-dark {
    background-color: #A01830;
}
.hover\:bg-brill-red-dark:hover {
    background-color: #A01830;
}
</style>
